import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getNewsById, news } from '../data/news';
import Footer from '../sections/Footer';

const gridAnim = keyframes`from{background-position:0 0}to{background-position:0 60px}`;
const fadeUp   = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}`;
const blink    = keyframes`0%,100%{opacity:1}50%{opacity:.2}`;

const Page = styled.div`min-height:100vh;overflow-x:hidden;background:#02020E;padding-top:72px;`;
const GridBg = styled.div`
  position:fixed;inset:0;pointer-events:none;z-index:0;
  background-image:
    linear-gradient(rgba(0,163,255,.02) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,163,255,.02) 1px,transparent 1px);
  background-size:60px 60px;animation:${gridAnim} 22s linear infinite;
`;

/* Hero */
const Hero = styled.div`
  position:relative;z-index:2;padding:60px 80px 56px;
  border-bottom:1px solid rgba(255,255,255,.05);overflow:hidden;
  &::after{content:'';position:absolute;inset:0;
    background:linear-gradient(to right,rgba(2,2,14,.95) 50%,transparent 100%);
    pointer-events:none}
  @media(max-width:900px){padding:50px 24px 44px}
  @media(max-width:480px){padding:36px 16px 32px}
`;
const EmojiFloat = styled.div`
  position:absolute;right:6%;top:50%;transform:translateY(-50%);
  font-size:clamp(80px,14vw,160px);opacity:.08;pointer-events:none;z-index:1;
  @media(max-width:700px){display:none}
`;
const HeroInner = styled.div`
  max-width:900px;margin:0 auto;position:relative;z-index:2;
  animation:${fadeUp} .5s ease both;
`;
const BackBtn = styled(Link)`
  display:inline-flex;align-items:center;gap:7px;
  font-family:'Space Mono',monospace;font-size:10px;letter-spacing:.22em;
  color:rgba(255,255,255,.4);text-decoration:none;text-transform:uppercase;
  transition:color .25s;margin-bottom:28px;
  &:hover{color:#00A3FF}
`;
const CatBadge = styled.span`
  display:inline-flex;align-items:center;gap:6px;
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.26em;
  color:${p=>p.$c};border:1px solid ${p=>p.$c}33;padding:4px 12px;text-transform:uppercase;
  margin-bottom:22px;
  &::before{content:'';width:5px;height:5px;background:${p=>p.$c};border-radius:50%;animation:${blink} 2s infinite}
`;
const PostTitle = styled.h1`
  font-family:'Bebas Neue',sans-serif;
  font-size:clamp(36px,8vw,96px);line-height:.9;letter-spacing:.04em;
  color:#fff;margin-bottom:20px;
`;
const Meta = styled.div`
  display:flex;align-items:center;gap:18px;flex-wrap:wrap;
`;
const MetaItem = styled.span`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.18em;
  color:rgba(255,255,255,.28);text-transform:uppercase;
`;

/* Body */
const Body = styled.div`
  position:relative;z-index:2;
  max-width:900px;margin:0 auto;
  padding:60px 80px 80px;
  @media(max-width:900px){padding:40px 24px 60px}
  @media(max-width:480px){padding:28px 16px 48px}
  @media(max-width:480px){padding:28px 16px 48px}
`;
const Lead = styled.p`
  font-family:'Rajdhani',sans-serif;font-size:19px;line-height:1.8;
  @media(max-width:480px){font-size:16px}
  color:rgba(255,255,255,.7);margin-bottom:36px;
  border-left:3px solid #00A3FF;padding-left:24px;
`;
const Content = styled.div`
  font-family:'Rajdhani',sans-serif;font-size:16px;line-height:1.9;
  color:rgba(255,255,255,.55);
  p{margin-bottom:1.4em}
`;

/* More news */
const MoreSection = styled.div`
  position:relative;z-index:2;
  max-width:1360px;margin:0 auto;
  padding:0 80px 80px;
  border-top:1px solid rgba(255,255,255,.05);
  padding-top:56px;
  @media(max-width:900px){padding:40px 24px 60px}
  @media(max-width:480px){padding:28px 16px 48px}
  @media(max-width:480px){padding:28px 16px 48px}
`;
const MoreTitle = styled.h2`
  font-family:'Bebas Neue',sans-serif;font-size:28px;letter-spacing:.08em;
  color:#fff;margin-bottom:20px;
`;
const MoreGrid = styled.div`
  display:grid;grid-template-columns:repeat(3,1fr);gap:3px;
  @media(max-width:900px){grid-template-columns:1fr 1fr}
  @media(max-width:560px){grid-template-columns:1fr}
`;
const MoreCard = styled(Link)`
  display:flex;align-items:flex-start;gap:16px;padding:20px 24px;
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.06);
  text-decoration:none;transition:all .3s;
  &:hover{background:rgba(255,255,255,.06);border-color:rgba(0,163,255,.2);transform:translateY(-2px)}
`;
const MoreEmoji = styled.div`font-size:28px;flex-shrink:0;margin-top:2px`;
const MoreInfo = styled.div`flex:1;`;
const MoreCat = styled.div`
  font-family:'Space Mono',monospace;font-size:8px;letter-spacing:.18em;
  color:${p=>p.$c};text-transform:uppercase;margin-bottom:4px;
`;
const MoreCardTitle = styled.div`
  font-family:'Rajdhani',sans-serif;font-weight:700;font-size:14px;
  color:rgba(255,255,255,.7);line-height:1.4;
  transition:color .25s;
  ${MoreCard}:hover &{color:#fff}
`;

const EMOJI_MAP = {'Estúdio':'🏢','Jogos':'🎮','Desenvolvimento':'⚙️','XR':'🥽','Lançamento':'🚀','Comunidade':'💬'};

const NotFoundWrap = styled.div`
  position:relative;z-index:2;display:flex;flex-direction:column;
  align-items:center;justify-content:center;min-height:60vh;
  text-align:center;gap:20px;
  h1{font-family:'Bebas Neue',sans-serif;font-size:60px;color:#fff}
  p{color:rgba(255,255,255,.4);font-family:'Rajdhani',sans-serif;font-size:16px}
`;
const Btn = styled(Link)`
  font-family:'Rajdhani',sans-serif;font-weight:700;font-size:13px;letter-spacing:.2em;
  text-transform:uppercase;padding:13px 32px;text-decoration:none;
  border:1px solid rgba(255,255,255,.15);color:rgba(255,255,255,.5);
  clip-path:polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%);
  transition:all .3s;&:hover{border-color:rgba(0,163,255,.4);color:#00A3FF}
`;

export default function NewsDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const item = getNewsById(id);

  if (!item) return (
    <Page>
      <GridBg/>
      <NotFoundWrap>
        <h1>{t('newsDetail.notFound')}</h1>
        <p>{t('newsDetail.notFoundDesc')}</p>
        <Btn to="/news">{t('newsDetail.notFoundBack')}</Btn>
      </NotFoundWrap>
    </Page>
  );

  const others = news.filter(n => n.id !== item.id).slice(0, 3);

  return (
    <Page>
      <GridBg/>

      <Hero>
        <EmojiFloat>{EMOJI_MAP[item.category] || '📰'}</EmojiFloat>
        <HeroInner>
          <BackBtn to="/news">{t('newsDetail.backBtn')}</BackBtn>
          <div><CatBadge $c={item.categoryColor}>{item.category}</CatBadge></div>
          <PostTitle>{item.title}</PostTitle>
          <Meta>
            <MetaItem>{item.date}</MetaItem>
            <MetaItem>·</MetaItem>
            <MetaItem>{t('newsDetail.readTime', { minutes: item.readTime })}</MetaItem>
            <MetaItem>·</MetaItem>
            <MetaItem>Guebly Games</MetaItem>
          </Meta>
        </HeroInner>
      </Hero>

      <Body>
        <Lead>{item.excerpt}</Lead>
        <Content>
          {(item.content || item.excerpt).split('\n\n').map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </Content>
      </Body>

      {others.length > 0 && (
        <MoreSection>
          <MoreTitle>{t('newsDetail.moreNews')}</MoreTitle>
          <MoreGrid>
            {others.map(n => (
              <MoreCard key={n.id} to={`/news/${n.id}`}>
                <MoreEmoji>{EMOJI_MAP[n.category] || '📰'}</MoreEmoji>
                <MoreInfo>
                  <MoreCat $c={n.categoryColor}>{n.category}</MoreCat>
                  <MoreCardTitle>{n.title}</MoreCardTitle>
                </MoreInfo>
              </MoreCard>
            ))}
          </MoreGrid>
        </MoreSection>
      )}

      <Footer/>
    </Page>
  );
}
